import { Chess } from 'chess.js';
import { fenAtPlyFromStart } from 'react-chess-core';
import type { AnalysisContext } from 'react-chess-replay-trainer';
import type { CourseLineSpec } from './useCourseLineTrainer';
import type { CoursePhase, TrainSide } from './types';

export type LineTrainMode = TrainSide | 'both';

export const turnFromFen = (fen: string): TrainSide =>
  fen.trim().split(/\s+/)[1] === 'b' ? 'b' : 'w';

/** Side to move at the first ply of a course line segment. */
export const firstPlyTrainSide = (startFen: string): TrainSide =>
  turnFromFen(startFen);

export const boardOrientationForTrainSide = (
  trainSide: TrainSide,
): 'white' | 'black' => (trainSide === 'b' ? 'black' : 'white');

/**
 * Default quiz side for a lesson. Always prefer {@link lessonTrainSide} — middlegame /
 * endgame lines are shifted so the opponent moves first at {@link startFen}, and using
 * the side-to-move there would quiz the opponent's moves.
 */
export const defaultLineTrainMode = (
  _coursePhase: CoursePhase,
  _startFen: string,
  lessonTrainSide: TrainSide,
): LineTrainMode => lessonTrainSide;

/** Ply on the line whose FEN matches `fen`, or null when `fen` is off-line (e.g. a miss). */
export const plyIndexForLineFen = (
  startFen: string,
  movesUci: string[],
  fen: string,
): number | null => {
  const target = fen.trim();
  for (let ply = 0; ply <= movesUci.length; ply++) {
    if (fenAtPlyFromStart(startFen, movesUci, ply) === target) {
      return ply;
    }
  }
  return null;
};

/** 0-based indices to quiz for the chosen train mode. */
export const lineTrainIndices = (
  line: Pick<CourseLineSpec, 'startFen' | 'movesUci'>,
  mode: LineTrainMode,
): number[] => {
  if (mode === 'both') {
    return line.movesUci.map((_, index) => index);
  }

  const indices: number[] = [];
  const chess = new Chess(line.startFen);
  for (let index = 0; index < line.movesUci.length; index++) {
    if ((chess.turn() as TrainSide) === mode) {
      indices.push(index);
    }
    try {
      applyUci(chess, line.movesUci[index]);
    } catch {
      break;
    }
  }
  return indices;
};

const applyUci = (chess: Chess, uci: string): void => {
  const from = uci.slice(0, 2);
  const to = uci.slice(2, 4);
  const promotion = uci.length > 4 ? uci[4] : undefined;
  const move = chess.move({ from, to, promotion });
  if (!move) {
    throw new Error(`Illegal UCI move: ${uci}`);
  }
};

/** FEN after applying the first `ply` moves from `startFen`. */
export { fenAtPlyFromStart };

export const buildCourseAnalysisContext = (
  startFen: string,
  movesUci: string[],
  plyIndex: number,
  boardOrientation: 'white' | 'black',
): AnalysisContext => ({
  initialFen: startFen,
  solutionMoves: movesUci,
  currentPly: Math.max(0, Math.min(plyIndex, movesUci.length)),
  boardOrientation,
});
